import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Shield, Search, Printer, User, Pencil, Save, RotateCcw, Lock, Eye, X } from "lucide-react";
import { toast } from "sonner";

const ENFORCE_META = {
  editable:   { label: "EDITABLE",   className: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Pencil, hint: "Toggle any role except admin." },
  admin_only: { label: "ADMIN ONLY", className: "bg-slate-100 text-slate-500 border-slate-200",       Icon: Lock,   hint: "Hard-locked to admin in code (safety rail)." },
  view:       { label: "VIEW",       className: "bg-slate-100 text-slate-500 border-slate-200",       Icon: Eye,    hint: "Informational — no server gate to change." },
};

export default function RolesPermissions() {
  const nav = useNavigate();
  const [data, setData] = useState({ roles: [], matrix: [] });
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  const [editMode, setEditMode] = useState(false);
  // Draft matrix during edit — flat cap_key → Set(roles)
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data: r } = await api.get("/admin/roles");
    setData(r);
    return r;
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const filteredMatrix = useMemo(() => {
    if (!data.matrix) return [];
    return data.matrix.map((section) => ({
      ...section,
      capabilities: section.capabilities.filter((c) => {
        if (q.trim()) {
          const s = q.toLowerCase();
          if (!c.label.toLowerCase().includes(s) && !c.key.toLowerCase().includes(s)) return false;
        }
        if (filterRole !== "all") {
          const roles = draft ? draft[c.key] : c.roles;
          if (!roles.includes(filterRole)) return false;
        }
        return true;
      }),
    })).filter((s) => s.capabilities.length > 0);
  }, [data.matrix, q, filterRole, draft]);

  const totalCapabilities = data.matrix?.reduce((n, s) => n + s.capabilities.length, 0) || 0;
  const editableCount = data.matrix?.reduce((n, s) => n + s.capabilities.filter((c) => c.enforce === "editable").length, 0) || 0;

  const startEdit = () => {
    // Snapshot current roles into a draft map
    const d = {};
    data.matrix.forEach((s) => s.capabilities.forEach((c) => { d[c.key] = [...c.roles]; }));
    setDraft(d);
    setEditMode(true);
  };

  const cancelEdit = () => { setDraft(null); setEditMode(false); };

  const toggleCell = (capKey, roleKey, enforce) => {
    if (enforce !== "editable") return;
    if (roleKey === "admin" || roleKey === "admin_addl") return; // admin + admin_addl always retain everything
    setDraft((prev) => {
      const cur = new Set(prev[capKey] || []);
      cur.has(roleKey) ? cur.delete(roleKey) : cur.add(roleKey);
      cur.add("admin");
      cur.add("admin_addl");
      return { ...prev, [capKey]: Array.from(cur) };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      // Only send editable rows — the endpoint ignores locked ones anyway, but this keeps traffic clean
      const editable = {};
      data.matrix.forEach((s) => s.capabilities.forEach((c) => {
        if (c.enforce === "editable" && draft[c.key]) editable[c.key] = draft[c.key];
      }));
      const { data: r } = await api.put("/admin/roles/matrix", { matrix: editable });
      toast.success(r.changed?.length ? `Saved — ${r.changed.length} capability change(s)` : "No changes to save");
      await load();
      setEditMode(false); setDraft(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const resetAll = async () => {
    if (!confirm("Reset the entire matrix to code defaults? This affects all editable capabilities immediately.")) return;
    setBusy(true);
    try {
      await api.post("/admin/roles/matrix/reset");
      toast.success("Matrix reset to defaults");
      await load();
      setEditMode(false); setDraft(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="roles-permissions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 print:hidden">
        <div>
          <button onClick={() => nav("/users")} className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-800 mb-2" data-testid="back-to-users-btn">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to User Management
          </button>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Access Control</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Roles &amp; Permissions</h1>
          <p className="text-slate-500 text-[13px] mt-1">
            {editMode
              ? <>Editing — toggle roles per capability, then <b>Save</b>. Admin always keeps every capability.</>
              : <>Live matrix — every checkmark reflects the backend's current gates. Toggle a role in <b>Edit</b> mode to tighten or loosen access.</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editMode ? (
            <>
              <Button variant="outline" onClick={() => window.print()} data-testid="print-roles-btn"><Printer className="h-4 w-4 mr-2" /> Print / Save as PDF</Button>
              <Button variant="outline" onClick={resetAll} disabled={busy} data-testid="reset-matrix-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset to defaults</Button>
              <Button onClick={startEdit} className="bg-brand hover:bg-brand-dark text-white" data-testid="edit-matrix-btn"><Pencil className="h-4 w-4 mr-2" /> Edit Matrix</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={cancelEdit} disabled={busy} data-testid="cancel-edit-btn"><X className="h-4 w-4 mr-2" /> Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="save-matrix-btn"><Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save changes"}</Button>
            </>
          )}
        </div>
      </div>

      {/* Search + role filter */}
      <Card className="p-4 border-slate-200 rounded-md bg-white print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search permissions…" className="w-full h-10 pl-9 pr-3 rounded-md bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-slate-300" data-testid="roles-search" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 shrink-0">Show granted to:</span>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger data-testid="roles-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any role</SelectItem>
                {data.roles.map((r) => <SelectItem key={r.key} value={r.key} className="capitalize">{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 text-[11.5px] text-slate-500">{data.roles.length} roles · {totalCapabilities} capabilities across {data.matrix?.length || 0} sections · {editableCount} editable</div>
      </Card>

      {/* Role catalog cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="role-catalog">
        {data.roles.map((r) => (
          <Card key={r.key} className="p-4 border-slate-200 rounded-md bg-white" data-testid={`role-card-${r.key}`}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-[#7C1D3A]/10 text-[#7C1D3A] flex items-center justify-center">
                <User className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[15px] capitalize">{r.name}</div>
                <div className="text-[12.5px] text-slate-500 mt-0.5">{r.description}</div>
                <div className="mt-2 text-[10.5px] font-mono uppercase tracking-widest text-slate-400">{r.key}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Capability matrix */}
      <Card className="border-slate-200 rounded-md bg-white overflow-hidden" data-testid="capability-matrix">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-heading font-bold text-lg">Capability Matrix</div>
            <div className="text-[12px] text-slate-500">
              <span className="inline-flex items-center gap-1 mr-3"><span className="inline-block h-3 w-3 rounded-sm bg-[#7C1D3A]"></span> granted</span>
              <span className="inline-flex items-center gap-1 mr-3"><Lock className="h-3 w-3 text-slate-400" /> locked to admin</span>
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3 text-slate-400" /> view-only capability</span>
            </div>
          </div>
          {editMode && <div className="text-[12px] text-emerald-700 font-semibold">Edit mode — click any green cell to toggle. Admin always stays granted.</div>}
        </div>
        <div className="overflow-auto max-h-[calc(100vh-260px)]" data-testid="capability-matrix-scroll">
          <table className="opsiq-table opsiq-sticky-head">
            <thead>
              <tr>
                <th className="bg-slate-50" style={{ minWidth: 340, position: "sticky", left: 0, zIndex: 3 }}>Capability</th>
                {data.roles.map((r) => (
                  <th key={r.key} className="text-center capitalize" style={{ minWidth: 110 }}>{r.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredMatrix.map((section) => (
                <>
                  <tr key={`sec-${section.section}`} className="bg-slate-50">
                    <td colSpan={data.roles.length + 1} className="font-heading font-bold uppercase tracking-widest text-[11.5px] text-brand py-2">{section.section}</td>
                  </tr>
                  {section.capabilities.map((c) => {
                    const meta = ENFORCE_META[c.enforce] || ENFORCE_META.view;
                    const currentRoles = draft ? (draft[c.key] || []) : c.roles;
                    return (
                      <tr key={c.key} data-testid={`cap-row-${c.key}`}>
                        <td>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{c.label}</div>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9.5px] font-semibold uppercase tracking-wider ${meta.className}`} title={meta.hint}>
                              <meta.Icon className="h-2.5 w-2.5" /> {meta.label}
                            </span>
                          </div>
                          <div className="text-[11px] font-mono text-slate-400">{c.key}</div>
                        </td>
                        {data.roles.map((r) => {
                          const has = currentRoles.includes(r.key);
                          const isAdminCol = r.key === "admin";
                          const clickable = editMode && c.enforce === "editable" && !isAdminCol;
                          const grantedClass = has ? "bg-[#7C1D3A] text-white" : "border border-slate-200";
                          const lockedClass = c.enforce !== "editable" ? "opacity-70" : "";
                          return (
                            <td key={r.key} className="text-center">
                              <button
                                type="button"
                                disabled={!clickable}
                                onClick={() => toggleCell(c.key, r.key, c.enforce)}
                                title={
                                  isAdminCol ? "Admin always retains this capability" :
                                  c.enforce !== "editable" ? meta.hint :
                                  editMode ? "Click to toggle" : "Enter edit mode to change"
                                }
                                data-testid={`cap-${c.key}-${r.key}${has ? "-on" : "-off"}`}
                                className={`inline-flex items-center justify-center h-5 w-5 rounded text-[11px] font-bold ${grantedClass} ${lockedClass} ${clickable ? "cursor-pointer hover:ring-2 hover:ring-brand/40" : "cursor-default"}`}
                              >
                                {has ? "✓" : ""}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              ))}
              {filteredMatrix.length === 0 && (
                <tr><td colSpan={data.roles.length + 1} className="text-center py-12 text-slate-500">No capabilities match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
